import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import Tokens from './pages/Tokens';
import Milestones from './pages/Milestones';
import Recovery from './pages/Recovery';
import Subscriptions from './pages/Subscriptions';
import Transactions from './pages/Transactions';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import ApiStatus from './pages/ApiStatus';
import Graveyard from './pages/Graveyard';
import Alerts from './pages/Alerts';
import WalletMonitor from './pages/WalletMonitor';
import Frontrun from './pages/Frontrun';
import FrontrunEventDetail from './pages/FrontrunEventDetail';
import Pings from './pages/Pings';
import TokenDetail from './pages/TokenDetail';
import TradingWallets from './pages/TradingWallets';
import MasterChef from './pages/MasterChef';
import MasterChefLayout from './components/masterchef/MasterChefLayout';
import MasterChefAnalytics from './pages/MasterChefAnalytics';
import MasterChefActivity from './pages/MasterChefActivity';
import MasterChefSettings from './pages/MasterChefSettings';
import MasterChefEma from './pages/MasterChefEma';
import MasterChefStaged from './pages/MasterChefStaged';
import MasterChefDataset from './pages/MasterChefDataset';
import EntryLogs from './pages/EntryLogs';
import VolumeMonitor from './pages/VolumeMonitor';
import UserStrategies from './pages/UserStrategies';
import TokenAggregator from './pages/TokenAggregator';
// import AllTimeLow from './pages/AllTimeLow'; // ATL feature paused

// Layout
import Layout from './components/Layout';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
            <Route index element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="groups" element={<Groups />} />
            <Route path="tokens" element={<Tokens />} />
            <Route path="token/:chain/:address" element={<TokenDetail />} />
            <Route path="aggregator" element={<TokenAggregator />} />
            <Route path="milestones" element={<Milestones />} />
            <Route path="recovery" element={<Recovery />} />
            <Route path="lifecycle" element={<Graveyard />} />
            {/* <Route path="atl" element={<AllTimeLow />} /> ATL feature paused */}
            <Route path="deposits" element={<Navigate to="/transactions" replace />} />
            <Route path="wallets" element={<Navigate to="/subscriptions" replace />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="wallet-monitor" element={<WalletMonitor />} />
            <Route path="frontrun" element={<Frontrun />} />
            <Route path="frontrun/events/:id" element={<FrontrunEventDetail />} />
            <Route path="pings" element={<Pings />} />
            <Route path="trading/masterchef" element={<MasterChefLayout />}>
              <Route index element={<MasterChefAnalytics />} />
              <Route path="positions" element={<MasterChef />} />
              <Route path="analytics" element={<Navigate to="/trading/masterchef" replace />} />
              <Route path="ema" element={<MasterChefEma />} />
              <Route path="staged" element={<MasterChefStaged />} />
              <Route path="activity" element={<MasterChefActivity />} />
              <Route path="settings" element={<MasterChefSettings />} />
            </Route>
            <Route path="trading/volume-monitor" element={<VolumeMonitor />} />
            <Route path="trading/user-strategies" element={<UserStrategies />} />
            <Route path="trading/wallets" element={<TradingWallets />} />
            <Route path="datasets/masterchef" element={<MasterChefDataset />} />
            <Route path="datasets/entry-logs" element={<EntryLogs />} />
            <Route path="settings" element={<Settings />} />
            <Route path="system/api" element={<ApiStatus />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
